import type { HostResourcesSnapshot } from "@afar-research/contracts";

/**
 * How much of the automatic share an Environment takes. These are preferences, not traffic
 * percentages: a weight only scales a machine's score against the others in the same decision.
 *
 * `manual-only` is not a weight of zero with a different name. Zero would still be a machine the
 * balancer considered and rejected; `manual-only` says the user reserved it for placements they
 * make themselves, which is why it never appears in an automatic candidate set at all.
 */
export const LOAD_BALANCING_PREFERENCES = [
  "prefer",
  "normal",
  "less-often",
  "manual-only",
] as const;
export type LoadBalancingPreference = (typeof LOAD_BALANCING_PREFERENCES)[number];

export const DEFAULT_LOAD_BALANCING_PREFERENCE: LoadBalancingPreference = "normal";

const PREFERENCE_WEIGHTS: Record<LoadBalancingPreference, number> = {
  prefer: 2,
  normal: 1,
  "less-often": 0.5,
  "manual-only": 0,
};

export function loadBalancingWeight(preference: LoadBalancingPreference): number {
  return PREFERENCE_WEIGHTS[preference];
}

export function isAutomaticallyEligible(preference: LoadBalancingPreference): boolean {
  return preference !== "manual-only";
}

/**
 * A sample older than this describes a machine that has since had time to fill up. Stale evidence
 * is dropped rather than discounted: placing work on a machine because of what it looked like a
 * minute ago is the failure this whole rule exists to avoid.
 */
export const RESOURCE_SAMPLE_MAX_AGE_MS = 15_000;
/** Clock skew tolerance. Times are client receipt times, so this only absorbs local jitter. */
const RESOURCE_SAMPLE_MAX_SKEW_MS = 5_000;
/** Above this, the machine is busy enough that adding a turn would make things worse for both. */
const CPU_SATURATION = 0.95;

export interface LoadBalancingCandidate {
  readonly environmentId: string;
  readonly resources: HostResourcesSnapshot | null;
  /**
   * When this client received the sample. Two machines' own clocks cannot be compared, so
   * freshness is judged by arrival here rather than by the `sampledAt` each machine wrote.
   */
  readonly receivedAt?: number;
  readonly weight: number;
}

function candidateScore(candidate: LoadBalancingCandidate, now: number): number {
  const { resources, weight } = candidate;
  const sampledAt = candidate.receivedAt ?? resources?.sampledAt ?? 0;
  if (
    !resources ||
    !Number.isFinite(weight) ||
    weight <= 0 ||
    now - sampledAt > RESOURCE_SAMPLE_MAX_AGE_MS ||
    sampledAt > now + RESOURCE_SAMPLE_MAX_SKEW_MS ||
    resources.cpuUtilization === null ||
    resources.cpuUtilization >= CPU_SATURATION ||
    resources.totalMemoryBytes <= 0 ||
    resources.cpuCount <= 0
  ) {
    return 0;
  }
  // Headroom, not usage: a 32-core machine at 50% has more room than a 4-core machine at 50%,
  // and memory is weighted alongside it so a machine that is swapping does not win on cores.
  const cpuHeadroom = (1 - resources.cpuUtilization) * resources.cpuCount;
  const memoryHeadroom = resources.availableMemoryBytes / resources.totalMemoryBytes;
  return cpuHeadroom * memoryHeadroom * weight;
}

/**
 * Picks the Environment with the most weighted headroom, or null when no candidate has usable
 * evidence. Callers pass only Environments that are connected, host the project, and carry the
 * selected Agent Provider; this rule judges capacity and nothing else.
 *
 * Returning null is a real answer and means "ask the user", never "use the first one". An
 * Environment chosen without evidence is the same bug as an Environment chosen from stale
 * evidence, one step further from anything the user can see.
 */
export function chooseLoadBalancedEnvironment(
  candidates: ReadonlyArray<LoadBalancingCandidate>,
  now: number,
): string | null {
  let selected: string | null = null;
  let bestScore = 0;
  for (const candidate of candidates) {
    const score = candidateScore(candidate, now);
    if (score > bestScore) {
      bestScore = score;
      selected = candidate.environmentId;
    }
  }
  return selected;
}
